<template>
    <calendar-month>
        <weekday-labels>
            <weekday-label
                v-for="(day, index) in weekDayNames"
                :key="index"
            >
                {{ day }}
            </weekday-label>
        </weekday-labels>

        <calendar-week
            v-for="(week, index) in weeks"
            :key="'week' + index"
            :style="getWeekStyle(week)"
        >
            <calendar-day
                v-for="(day, dayIndex) in week"
                :key="index + 'day' + dayIndex"
                v-qa="day.getDate()"
                :class="getDateClass(day)"
                @click="pick(day)"
            >
                <div class="label">
                    {{ day.getDate() }}
                </div>
            </calendar-day>
        </calendar-week>
    </calendar-month>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { chunk } from 'remeda'
import { DAYS_ALL, isAllowedWeekdayDate } from './date'

interface Props {
    modelValue: Date | null
    dateView: Date
    min?: Date | null
    max?: Date | null
    allowedWeekdays?: number
    disallowedDates?: Date[]
    allowedDates?: Date[]
}

const props = withDefaults(defineProps<Props>(), {
    min: undefined,
    max: undefined,
    allowedWeekdays: DAYS_ALL,
    disallowedDates: () => [],
    allowedDates: undefined,
})

const emit = defineEmits<{
    'update:modelValue': [payload: Date]
}>()

const pick = (day: Date) => {
    emit('update:modelValue', day)
}

const year = computed(() => props.dateView.getFullYear())
const month = computed(() => props.dateView.getMonth())

const weeks = computed(() => {
    const days: Date[] = []

    const daysInMonth = 32 - new Date(year.value, month.value, 32).getDate()

    for (let i = 1; i <= daysInMonth; i += 1) {
        days.push(new Date(year.value, month.value, i))
    }

    let firstDay = props.dateView.getDay()

    if (firstDay === 0) {
        firstDay = 7
    }

    const firstWeek = days.splice(0, 8 - firstDay)

    return [firstWeek, ...chunk(days, 7)]
})

const getWeekStyle = (week: Date[]) => {
    if (week.length !== 7) {
        return `width: ${(week.length * 100) / 7}%`
    }

    return undefined
}

const isNotPickableMinMax = (date: Date) => {
    if (props.min && props.max) {
        return date < props.min || date > props.max
    } else if (props.min) {
        return date < props.min
    } else if (props.max) {
        return date > props.max
    } else {
        return false
    }
}

const isNotPickableWeekday = (date: Date) =>
    !isAllowedWeekdayDate(props.allowedWeekdays, date)

const disallowedDatesCurrent = computed(() =>
    props.disallowedDates.filter(
        (date) =>
            date.getFullYear() === props.dateView.getFullYear() &&
            date.getMonth() === props.dateView.getMonth()
    )
)

const allowedDatesCurrent = computed(() =>
    props.allowedDates?.filter(
        (date) =>
            date.getFullYear() === props.dateView.getFullYear() &&
            date.getMonth() === props.dateView.getMonth()
    )
)

const isNotPickableExplicitly = (date: Date) =>
    disallowedDatesCurrent.value.some(
        (disallowedDate) => disallowedDate.getDate() === date.getDate()
    ) ||
    Boolean(
        allowedDatesCurrent.value &&
            !allowedDatesCurrent.value.some(
                (allowedDate) => allowedDate.getDate() === date.getDate()
            )
    )

const isNotPickable = (date: Date) =>
    isNotPickableMinMax(date) ||
    isNotPickableWeekday(date) ||
    isNotPickableExplicitly(date)

const getDateClass = (date: Date) => {
    let dateClass = ''

    if (isNotPickable(date)) {
        dateClass = 'non-pickable'
    }

    if (props.modelValue && props.modelValue.getTime() === date.getTime()) {
        dateClass += ' selected'
    }

    return dateClass
}

// TODO use localized
const weekDayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
</script>

<style lang="scss" scoped>
calendar-month {
    display: block;
    margin: 0 12px;
    // height: 100%;
    user-select: none;

    month-header {
        float: left;
        height: 64px;
        display: flex;
        align-items: center;
        line-height: 35px;
        padding: 0 16px;
    }

    weekday-labels {
        display: block;
        margin-bottom: 8px;
        width: 100%;
        font-weight: 500;

        weekday-label {
            display: inline-block;
            margin: 0;
            width: 14.28%;
            height: 36px;
            padding-top: 14px;
            text-align: center;
        }
    }

    calendar-week {
        position: relative;
        margin: 0;
        display: flex;
        width: 100%;
        z-index: 0;

        &:first-of-type {
            margin-left: auto;
        }

        &:last-of-type {
            margin-right: auto;
        }

        calendar-day {
            position: relative;
            width: 41px;
            height: 41px;
            display: flex;
            align-items: center;
            justify-content: center;

            &.selected {
                color: var(--white);

                &::after {
                    position: absolute;
                    inset: 0;
                    margin: auto;
                    width: 41px;
                    height: 41px;
                    content: '';
                    z-index: -1;
                    background-color: var(--green);
                }
            }

            &.non-pickable {
                pointer-events: none;
                color: var(--grey-300);
            }

            .label {
                position: relative;
                height: 20px;
                display: flex;
                align-items: flex-end;
                justify-content: center;
                cursor: pointer;
                text-align: center;
            }
        }
    }
}
</style>
